import React, {useState, useEffect} from "react";

import './Team.members.dialog.scss';
import Modal from "react-modal";
import {useLazyQuery, useMutation} from "@apollo/client";
import gqlSchemaLibrary from "../../services/graphql.schemas";
import {Subject} from "rxjs";
import {useTranslation} from "react-i18next";
import {ModalLog} from "../../services/interactions/elements/modal";
import AuditTrail, {PAGES} from "../../services/interactions/audit.trail";

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: "none",
        padding: "0px",
        boxShadow: "0 3px 8px 0 rgba(0, 0, 0, 0.38)",
    }
};
Modal.setAppElement('#root');

function TeamMemberItem({member, onSelected}) {

    const handleClick = () => {
        onSelected(member);
    }

    return (
        <div className={`MemberItem`} onClick={handleClick}>
                <p className="ellipsis">
                    {member.name}
                </p>
                <p className="ellipsis">
                    {member.email}
                </p>
                <p className="capitalize">
                    {member.role}
                </p>
                <p>
                    {member.type}
                </p>
            </div>
    );
}

const _Modal = ModalLog(Modal);

function TeamMembersDialog({event, team, openModal, closeModal}) {
    const {t} = useTranslation(['common','teams','users']);
    const [members, setMembers] = useState([]);
    const [membersSelected, setMembersSelected] = useState([]);

    const [searchFocused, changeSearchFocused] = useState(false);
    const [search, changeSearch] = useState('');
    const [searchTerm] = useState(new Subject());

    const [getTeams] = useLazyQuery(gqlSchemaLibrary.teams.queries.getTeams, {
        onCompleted: (data) => {
            const _members = Object.assign([], members);
            const _membersSelected = Object.assign([], membersSelected);
            for (const t of data.getTeams) {
                if (t._id !== team._id) {
                    const d = {
                        _id: t._id,
                        name: t.name,
                        role: "",
                        email: "",
                        type: "Team"
                    };
                    _members.push(d);
                    if (team.teams.find(m => m._id === t._id)) {
                        _membersSelected.push(d);
                    }
                }
            }
            setMembersSelected(_membersSelected);
            setMembers(_members);
        },
        fetchPolicy: 'network-only'
    })

    const [getUsers] = useLazyQuery(gqlSchemaLibrary.clinicians.queries.getCliniciansByOrg, {
        onCompleted: (data) => {
            const _members = [];
            const _membersSelected = [];
            for (const m of data.getCliniciansByOrg) {
                const name = m.user.bio.lastname + ', ' + m.user.bio.firstname;
                const d = {
                    _id: m._id,
                    name: name,
                    role: m.role,
                    email: m.user.email,
                    type: "User"
                };
                _members.push(d);
                if (team.users.find(u => u._id === m._id)) {
                    _membersSelected.push(d);
                }
            }
            setMembersSelected(_membersSelected);
            setMembers(_members);
            getTeams();
        },
        fetchPolicy: 'cache-and-network'
    })

    useEffect(() => {
        if (openModal) {
            getUsers();
        }
    }, [openModal])

    const [editMembersTeam] = useMutation(gqlSchemaLibrary.teams.mutation.editMembersTeam, {
        onCompleted: ({ editTeam }) => {
            const users = membersSelected.filter((m) => m.type === 'User');
            const teams = membersSelected.filter((m) => m.type === 'Team');
            let _users = users.slice();
            let _teams = teams.slice();
            setMembersSelected([]);
            setMembers([]);
            changeSearch('')
            searchTerm.next('');
            closeModal(team, {
                users: _users,
                teams: _teams
            });
        },
        onError: (e) => {
            console.error('Update Error: ', e);
        }
    });

    const closeModalEmpty = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'closeModal', action: 'button modal', value: search, team_id: team._id});
        setMembersSelected([]);
        setMembers([]);
        changeSearch('')
        searchTerm.next('');
        closeModal(null, null);
    }

    const handleFocus = (bool) => {
        return () => {
            AuditTrail.sendLog(event, {tag: 'input', event: 'onSearch', action: 'input modal add members search teams', focus: bool, value: search, team_id: team._id});
            changeSearchFocused(bool);
        }
    }

    const handleSearch = (e) => {
        const value = e.target.value;
        changeSearch(value)
        searchTerm.next(value);
    }

    const onSelected = (member) => {
        const index = membersSelected.indexOf(member);
        const checked = index === -1;
        if (index === -1) {
            membersSelected.push(member);
        }
        AuditTrail.sendLog(event, {tag: 'row', event: 'onClick', action: 'selected modal add members', checked: checked, team_id: team._id, member_id: member._id});
        setMembersSelected(Object.assign([], membersSelected));
    }

    const filteredMembers = members.filter(t => {
        if (membersSelected.find((m) => m._id === t._id)) {
            return false;
        }
        if (!search) {
            return true;
        }
        const name = (t.name || '').toLowerCase();
        return name.includes(search.toLowerCase());
    }).sort((a, b) => {
        return a.name.localeCompare(b.name);
    })

    const onDeleteItem = (member) => {
        const index = membersSelected.indexOf(member);
        if (index > -1) {
            membersSelected.splice(index, 1);
        }
        AuditTrail.sendLog(event, {tag: 'row', event: 'onClick', action: 'selected modal add members', checked: false, team_id: team._id, member_id: member._id});
        setMembersSelected(Object.assign([], membersSelected));
    }

    const onSave = () => {
        const users = membersSelected.filter((m) => m.type === 'User').map((u) => u._id);
        const teams = membersSelected.filter((m) => m.type === 'Team').map((u) => u._id);
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button modal save', team_id: team._id, users: users, teams: teams});
        editMembersTeam({
            variables: {
                teamMembers: {
                    _id: team._id,
                    users: users,
                    teams: teams
                }
            }
        })
    }

    return (
        <_Modal
            isOpen={!!openModal}
            style={customStyles}
            onRequestClose={closeModalEmpty}
            contentLabel="Add members"
            label="MembersDialog"
            event={event}
            eventParams={{team_id: team._id}}>
            <div id="MembersDialog">
                <div className="__MembersTitle">
                    <h3>{t('teams:assign-unassign-to-team')}:</h3>
                    <p>{team.name}</p>
                </div>
                <div className="__Container">
                    <p className="__MembersSubTitle">
                        <b>{t('teams:assign-users-teams')}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-assign')}
                    </p>
                    <div className="InputComponent">
                        <input value={search} placeholder={t('teams:find-user-or-team')} required={false} onChange={handleSearch}
                           onFocus={handleFocus(true)} onBlur={handleFocus(false)}/>
                    </div>
                    <div className="__MembersList">
                        <div className="headers">
                            <div className="selectable">
                                <p>
                                    {t('name')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('users:email')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('users:role')}
                                </p>
                            </div>
                            <div className="selectable">
                                <p>
                                    {t('users:type')}
                                </p>
                            </div>
                        </div>
                        {
                            filteredMembers.map((member, index) => (
                                <TeamMemberItem key={index} member={member} onSelected={onSelected} />
                            ))
                        }
                    </div>
                    <p className="__MembersSubTitle">
                        <b>{t('teams:unassign-users-teams')}</b>
                    </p>
                    <p>
                        {t('teams:click-on-the-name-unassign')}
                    </p>
                    <div className="tags">
                        {
                            membersSelected.map((member, index) => (
                                <span key={'s-'+index} className="selectedMember">
                                    {member.name}
                                    <i className="material-icons" onClick={() => onDeleteItem(member)}>close</i>
                                </span>
                            ))
                        }
                    </div>
                    <div className="containerButtons">
                        <button className="primary gray" onClick={closeModalEmpty}>
                            {t('cancel')}
                        </button>
                        <button className="primary green" onClick={onSave}>
                            {t('add')}
                        </button>
                    </div>
                </div>
            </div>
        </_Modal>
    );
}

export default TeamMembersDialog;
